import { Callout } from './components/callout.tsx'
import { Xcelera } from './components/xcelera.tsx'

export const meta = () => [
	{ title: 'Concepts · xcelera docs' },
	{
		name: 'description',
		content:
			'The core terms: pages, audits, scores and metrics, schedules, credits, and archiving.',
	},
]

# Concepts

It's helpful to know a few core concepts to get the best out of <Xcelera />.

## Page

A page is a URL you monitor over time: your homepage, a product page, a
landing page you're about to launch. Each page has a name, a schedule, and a
**ref**, the short slug that identifies it in the dashboard, the API, and MCP
tools.

A page's device profile and region are part of what it measures, so every
audit of the page is comparable with the last. To measure the same URL on both
mobile and desktop, or from multiple locations, it's recommended to configure it as multiple pages. Changing a page's device or
region splits its trend; [Regions & Devices](/docs/regions-and-devices)
explains the trade-off.

## Audit

An audit is one measurement of a page at a point in time. It's the unit
everything else builds on: the dashboard trend is a series of audits, and an
[AI Diagnosis](/docs/ai-diagnosis) is a close reading of one audit.

To produce numbers stable enough to act on, an audit measures the page several
times under identical lab conditions and reports the median of every score and
metric. The full report you can open from an audit comes from a single
representative measurement, so its filmstrip and load details describe one
real page load rather than a statistical blend.
[Audit Stability](/docs/audit-stability) covers why this matters.

Audits are triggered four ways: manually from the dashboard, by the page's
schedule, through the [API & CLI](/docs/api-usage), or by an
[MCP](/docs/mcp) client. Audits triggered from CI are tagged with the branch
and commit they ran against.

Every audit moves through the same lifecycle: **Scheduled**, **Running**, then
**Succeeded** or **Failed**. Succeeded means the measurement was clean. If the
page itself misbehaved during measurement (a 404, an unexpected login wall),
the audit fails rather than reporting misleading numbers.

## Scores & metrics

A succeeded audit reports the four Lighthouse category scores on a 0–100
scale: Performance, Accessibility, Best Practices, and SEO. Behind the
performance score sit the individual metrics: Largest Contentful Paint (LCP),
Total Blocking Time (TBT), Cumulative Layout Shift (CLS), and others. These
numbers power the dashboard and every trend chart.

## Schedule

Each page has one schedule: none, daily at an hour you choose in your
timezone, or every N hours on paid plans. The schedule decides when the next
audit starts without you. [Scheduling](/docs/scheduling) covers the options.

## Credits

An audit costs one credit from your organization's monthly allowance. Only
successful audits are charged; a failed audit costs nothing. Credits reset at
the start of each usage period. [Usage & Billing](/docs/usage-and-billing) has
the allowances per plan.

## Archiving

Archiving retires a page: scheduled audits stop and the page leaves the
dashboard. Its audit history is retained but no longer surfaced. Archive from
the page's settings, via the API, or with an MCP tool.

<Callout type="warning">
	Archiving is not reversible. Adding the same URL again creates a new page with
	a fresh ref and no history.
</Callout>

## Next steps

- [Audit Stability](/docs/audit-stability) — how audits produce numbers you can trust
- [Regions & Devices](/docs/regions-and-devices) — device profiles and audit regions
- [Scheduling](/docs/scheduling) — audit frequency, timing, and timezones
- [Usage & Billing](/docs/usage-and-billing) — credit allowances and plans
